import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle, Check, CreditCard, FileText, IdCard, Lock, ReceiptEuro, Tag, Trash2,
  UserPlus,
} from 'lucide-react'
import { useI18n } from '../i18n'
import { supabase } from '../lib/supabase'
import { useStore } from '../data/store'
import { BLOCKING_STATUSES } from '../data/model'
import type { Booking, BookingStatus, Guest, Invoice, Payment } from '../data/model'
import { addDays, nights, todayISO } from '../lib/dates'
import { aehnlicheGaeste } from '../lib/guests'
import { prepaymentFor, prepaymentPolicyOf } from '../lib/pricing'
import { Badge, Button, Field, Input, Select, Sheet, cx } from '../ui/kit'
import CheckinCard from './CheckinCard'

/** A week is the usual hold in this market; the field is editable either way. */
const OPTION_DEFAULT_DAYS = 7

const STATUSES: BookingStatus[] = [
  'enquiry', 'quoted', 'option', 'confirmed', 'checked_in', 'checked_out', 'cancelled', 'no_show',
]

export const STATUS_TONE: Record<BookingStatus, 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'accent'> = {
  enquiry: 'neutral',
  quoted: 'info',
  option: 'warn',
  confirmed: 'ok',
  checked_in: 'accent',
  checked_out: 'neutral',
  cancelled: 'danger',
  no_show: 'danger',
}

export default function BookingSheet({
  booking, open, onClose, prefill,
}: {
  booking: Booking | null
  open: boolean
  onClose: () => void
  /**
   * Gastangaben aus einer eingefügten Anfrage. Füllen das Anlegeformular vor,
   * mehr nicht: angelegt wird der Gast erst, wenn der Vermieter darauf drückt.
   */
  prefill?: { first_name: string; last_name: string; email: string; phone: string }
}) {
  const { t, lang, formatMoney, formatDate, pick } = useI18n()
  const de = lang === 'de'
  const store = useStore()
  const navigate = useNavigate()
  const [draft, setDraft] = useState<Booking | null>(booking)
  const [guestQuery, setGuestQuery] = useState('')
  const [guestForm, setGuestForm] = useState(
    { email: '', address: '', postcode: '', city: '', language: 'de' })
  /**
   * Der Preisteil ist zugeklappt, weil er die Ausnahme ist. Wer nichts aushandelt,
   * soll nicht an zwei leeren Feldern vorbeiscrollen — wer schon etwas ausgehandelt
   * hat, muss es dagegen sofort sehen, sonst ist der Sonderpreis versteckt.
   */
  const [priceOpen, setPriceOpen] = useState(false)
  /** Die Rechnung, die es zu diesem Aufenthalt schon gibt — solange nachgefragt wird. */
  const [invoiceClash, setInvoiceClash] = useState<Invoice | null>(null)
  /** Das Anlegeformular für einen Gast, solange es offen ist. */
  const [newGuest, setNewGuest] = useState<
    { first_name: string; last_name: string; email: string; phone: string } | null>(null)
  /*
   * Was in einem Zahlenfeld steht, WÄHREND getippt wird.
   *
   * `Number('')` ist 0. Ohne diesen Zwischenschritt sprang das Feld beim Löschen
   * der Ziffer sofort auf „0" — und wer die Zahl nur ändern wollte, musste erst
   * die aufgedrängte Null wieder wegräumen. Auf dem Telefon, mit der Tastatur im
   * Weg, ist das eine Handbewegung zu viel für etwas so Kleines.
   *
   * Also darf das Feld leer sein, solange der Finger noch darauf liegt. Die
   * Buchung selbst bekommt in dieser Zeit keinen Wert — sie behält den alten,
   * denn eine Buchung mit „null Erwachsenen" ist nichts, was jemand meint.
   * Beim Verlassen räumt `zahlVerlassen` den Entwurf weg, und das Feld zeigt
   * wieder, was tatsächlich gespeichert ist.
   */
  const [zahlEntwurf, setZahlEntwurf] = useState<Record<string, string>>({})

  const leerLassen = (feld: string, wert: number) =>
    zahlEntwurf[feld] !== undefined ? zahlEntwurf[feld] : String(wert)

  const zahlTippen = (feld: string, roh: string, uebernehmen: (n: number) => void) => {
    setZahlEntwurf((z) => ({ ...z, [feld]: roh }))
    if (roh.trim() === '') return
    const n = Number(roh)
    if (Number.isFinite(n) && n >= 0) uebernehmen(Math.floor(n))
  }

  const zahlVerlassen = (feld: string) =>
    setZahlEntwurf((z) => {
      const rest = { ...z }
      delete rest[feld]
      return rest
    })

  useEffect(() => {
    setDraft(booking)
    // Ein halb getippter Zahlenentwurf gehört nicht in die nächste Buchung.
    setZahlEntwurf({})
    setGuestQuery('')
    setInvoiceClash(null)
    setPriceOpen(Boolean(booking?.nightly_rate_override != null || booking?.discount_amount))
    /*
     * Kam die Buchung aus einer eingefügten Anfrage, steht das Anlegeformular
     * schon offen und gefüllt da.
     *
     * WARUM DER GAST NICHT GLEICH ANGELEGT WIRD: der Namenszwang macht ihn zur
     * Bedingung fürs Speichern, nicht zum Nebenprodukt des Einfügens. Ein Modell,
     * das „Familie Berger" als Vor- und Nachnamen gelesen hat, liegt vielleicht
     * richtig — nachsehen kann das nur der Vermieter, und zwar bevor der Datensatz
     * in der Kartei steht, nicht danach.
     *
     * Absichtlich nur an `booking` gebunden: `prefill` wechselt mit derselben
     * Auswahl. Stünde es in den Abhängigkeiten, ginge das Formular wieder auf,
     * nachdem der Vermieter es weggeklickt hat.
     */
    setNewGuest(booking && !booking.guest_id && prefill ? { ...prefill } : null)
  }, [booking])

  const linkedGuest = store.guestById(draft?.guest_id ?? null)
  useEffect(() => {
    setGuestForm({
      email: linkedGuest?.email ?? '',
      address: linkedGuest?.address ?? '',
      postcode: linkedGuest?.postcode ?? '',
      city: linkedGuest?.city ?? '',
      language: linkedGuest?.language ?? 'de',
    })
  }, [linkedGuest?.id])

  const priced = useMemo(() => (draft ? store.price(draft) : null), [draft, store])

  const overlapping = useMemo(() => {
    if (!draft) return false
    return !store.isAvailable(draft.unit_id, draft.arrival, draft.departure, draft.id)
  }, [draft, store])

  const conflicts = useMemo(
    () => (draft ? store.conflictsFor(draft) : []), [draft, store])

  /**
   * Overlaps that are permitted but worth saying out loud: two guests may enquire
   * about the same week, so an enquiry or a quote does not block the unit. It should
   * still be impossible to write a second booking over one without noticing.
   */
  const softOverlaps = useMemo(() => {
    if (!draft) return []
    return store
      .overlappingBookings(draft.unit_id, draft.arrival, draft.departure, draft.id)
      .filter((b) => b.kind !== 'block' && !BLOCKING_STATUSES.includes(b.status))
  }, [draft, store])

  if (!draft) return null

  const unit = store.unitById(draft.unit_id)
  const guest = store.guestById(draft.guest_id)
  const isBlock = draft.kind === 'block'
  const stayNights = nights(draft.arrival, draft.departure)
  const paid = store.paidFor(draft.id)
  const outstanding = (priced?.totals.gross ?? 0) - paid

  /**
   * Eine Buchung braucht jemanden, der bucht. Ohne Namen ist der Balken im Kalender
   * eine Behauptung über belegte Nächte, die niemand einlösen kann — die Einheit ist
   * blockiert und keiner weiß, für wen. Der Store weist das ebenfalls ab; hier steht
   * es, damit man es sieht, bevor man vergeblich auf „Speichern" drückt.
   */
  const missingGuest = !isBlock && !draft.guest_id

  /*
   * Ein Aufenthalt ohne Nacht ist keiner.
   *
   * Das Mitrücken oben verhindert, dass dieser Zustand hier NEU entsteht. Diese
   * Prüfung fängt ab, was auf anderem Weg hereinkommt: ein von Hand getippter
   * Wert, ein Datensatz aus der Zeit vor dieser Änderung, ein Import. Sie ist
   * die letzte Station vor dem Speichern — dahinter fängt es erst die Datenbank
   * ab, und deren Fehlermeldung erklärt einem Vermieter gar nichts.
   */
  const badDates = draft.departure <= draft.arrival

  /**
   * Rechnungen, die es zu diesem Aufenthalt schon gibt.
   *
   * Stornierte zählen nicht: nach einer Gutschrift ist eine neue Rechnung der normale
   * Weg, und eine Rückfrage an dieser Stelle wäre eine Warnung vor dem Richtigen. Die
   * Gutschriften selbst sind keine Rechnungen über den Aufenthalt, sondern deren
   * Gegenstück. Entwürfe zählen sehr wohl — sie sind der häufigste Fall des Fehlers.
   */
  const bookingInvoices = store.db.invoices
    .filter((i) => i.booking_id === draft.id && i.type !== 'credit_note' && i.status !== 'cancelled')
    .sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''))

  /*
   * Was nach der Abrechnung nicht mehr geändert wird.
   *
   * Sobald zu dieser Buchung eine Rechnung AUSGESTELLT ist, sind Anreise, Abreise
   * und Einheit zu. Die Rechnung ist unveränderlich, sobald sie eine Nummer trägt —
   * eine Buchung, die danach still von ihrem eigenen Beleg abweicht, ist der Fehler,
   * den niemand bemerkt, bis das Finanzamt oder der Gast fragt.
   *
   * Gesperrt sind genau diese drei Felder und sonst keines: Status, Zahlungen,
   * Notizen, Kinderalter und Gastzuordnung müssen an einer abgerechneten Buchung
   * weiter änderbar sein — sonst ließe sich ein Gast mit Anzahlungsrechnung nicht
   * mehr einchecken.
   *
   * Ein Entwurf sperrt nicht. Er steht ohnehin schon weiter unten in der Liste der
   * vorhandenen Rechnungen und ist zum Umschreiben da.
   */
  const invoiceLock = store.invoiceLockFor(draft.id)
  const dateLocked = invoiceLock.length > 0
  const lockedBy = invoiceLock
    .map((i) => `${i.number ?? '—'} ${de ? 'vom' : 'of'} ${formatDate(i.issue_date)}`)
    .join(', ')

  /**
   * Steht die Buchung schon in der Kartei — oder lebt sie nur in diesem Blatt?
   *
   * Eine im Kalender aufgezogene Buchung gibt es bis zum ersten Speichern
   * nirgends sonst. Jeder Weg, der das Blatt verlässt, wirft sie damit weg.
   */
  const savedYet = store.db.bookings.some((b) => b.id === draft.id)

  /*
   * Könnte der, den der Vermieter gerade anlegt, schon in der Kartei stehen?
   *
   * `findDuplicates` räumt hinterher auf; das ist die zweitbeste Zeit. Die
   * beste ist, BEVOR die zweite Zeile entsteht — danach hängen an beiden
   * Buchungen, und das Zusammenführen ist Arbeit, die niemand gerne macht.
   *
   * Bewusst nicht blockierend: es gibt echte Fälle gleichen Namens, und ein
   * Vermieter, der seinen Gast kennt, darf nicht ausgesperrt werden. Der
   * Hinweis bietet den vorhandenen an, mehr nicht.
   */
  const verdacht = newGuest
    ? aehnlicheGaeste(newGuest, store.db.guests as never)
    : []

  const set = (patch: Partial<Booking>) => setDraft({ ...draft, ...patch })

  const prepaymentPolicy = prepaymentPolicyOf(store.db.tenant)
  const suggestedPrepayment = prepaymentFor(priced?.totals.gross ?? 0, prepaymentPolicy)

  const optionalFees = store.db.fees.filter(
    (f) => f.active && !f.mandatory && f.code !== 'tourist_tax'
      && (f.unit_id === null || f.unit_id === draft.unit_id))

  const guestMatches: Guest[] = guestQuery.trim().length < 2 ? [] : store.db.guests
    .filter((g) => `${g.first_name} ${g.last_name} ${g.email}`.toLowerCase()
      .includes(guestQuery.toLowerCase()))
    .slice(0, 5)

  const commitGuest = () => {
    if (!guest) return
    const changed =
      (guest.email ?? '') !== guestForm.email ||
      (guest.address ?? '') !== guestForm.address ||
      (guest.postcode ?? '') !== guestForm.postcode ||
      (guest.city ?? '') !== guestForm.city
    if (changed) void store.saveGuest({ ...guest, ...guestForm })
  }

  const save = async () => {
    commitGuest()
    // Nur schließen, wenn wirklich gespeichert wurde: sonst verschwindet das Blatt
    // samt Eingaben, und die Fehlermeldung dahinter erklärt etwas, das nicht mehr
    // sichtbar ist.
    if (await store.saveBooking(draft)) onClose()
  }

  const makeInvoice = async () => {
    setInvoiceClash(null)
    if (!(await store.saveBooking(draft))) return
    const id = await store.createInvoice(draft.id)
    onClose()
    if (id) navigate(`/document/invoice/${id}`)
  }

  const invoiceStateLabel = (i: Invoice) => (de
    ? { draft: 'Entwurf', sent: 'Offen', partly_paid: 'Teilweise bezahlt',
        paid: 'Bezahlt', overdue: 'Überfällig', cancelled: 'Storniert' }[i.status]
    : { draft: 'Draft', sent: 'Open', partly_paid: 'Partly paid',
        paid: 'Paid', overdue: 'Overdue', cancelled: 'Cancelled' }[i.status])

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          {isBlock ? t('status.block') : store.guestName(draft.guest_id)}
          <Badge tone={isBlock ? 'neutral' : STATUS_TONE[draft.status]}>
            {isBlock ? draft.reference : t(`status.${draft.status}` as never)}
          </Badge>
        </span>
      }
      footer={
        <>
          <Button variant="danger" size="md"
            onClick={async () => { await store.deleteBooking(draft.id); onClose() }}>
            <Trash2 size={16} />
          </Button>
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" className="flex-1" onClick={save}
            disabled={overlapping || missingGuest || badDates}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      {badDates && (
        <Warning tone="danger" text={t('booking.departureBeforeArrival')} />
      )}
      {overlapping && (
        <Warning tone="danger" text={t('booking.overlaps')} />
      )}
      {/* Kein Fehler, sondern der nächste Handgriff: das Blatt geht auch für eine
          eben erst aufgezogene Buchung auf, und dort ist „noch kein Gast" der
          Normalzustand — der Satz sagt deshalb, wo man ihn einträgt. */}
      {missingGuest && (
        <Warning
          tone="warn"
          text={de
            ? 'Ohne Gast lässt sich die Buchung nicht speichern — suchen Sie unten einen bestehenden Gast oder legen Sie mit „Neuen Gast anlegen" einen neuen an.'
            : 'A booking needs a guest before it can be saved — search for an existing guest below, or use “Create a new guest”.'}
        />
      )}
      {!overlapping && softOverlaps.length > 0 && (
        <Warning
          tone="warn"
          text={t('booking.softOverlap', {
            who: softOverlaps
              .map((b) => store.guestName(b.guest_id) || t(`status.${b.status}` as never))
              .join(', '),
          })}
        />
      )}
      {conflicts.length > 0 && (
        <Warning tone="warn" text={t('calendar.conflict')} />
      )}
      {priced?.warnings.map((w, i) => (
        <Warning
          key={i}
          tone="warn"
          text={
            w.code === 'min_nights' ? t('booking.minNights', { n: w.nights })
            : w.code === 'max_occupancy' ? t('booking.tooManyGuests', { max: w.max })
            : w.code === 'unknown_child_ages' ? t('touristTax.unknownAges')
            : t('calendar.free')
          }
        />
      ))}

      {/* Der Satz steht ÜBER den gesperrten Feldern und nicht in ihnen: ein graues
          Feld ohne Erklärung liest sich wie ein Fehler der App. Und er nennt den
          Ausweg mit — eine Sperre ohne genannten Ausweg wird umgangen, nicht
          befolgt. */}
      {dateLocked && (
        <p className="flex items-start gap-2 text-sm rounded-xl bg-surface-2 px-3 py-2.5">
          <Lock size={16} className="shrink-0 mt-0.5 text-muted" />
          <span>
            {de
              ? `Für diesen Aufenthalt ${invoiceLock.length > 1 ? 'sind Rechnungen' : 'ist Rechnung'} ${lockedBy} ausgestellt. Anreise, Abreise und Einheit sind damit festgeschrieben. Wenn sich der Aufenthalt wirklich geändert hat: Rechnung stornieren, Buchung ändern, neu ausstellen.`
              : `${invoiceLock.length > 1 ? 'Invoices' : 'Invoice'} ${lockedBy} ${invoiceLock.length > 1 ? 'have' : 'has'} been issued for this stay, so its arrival, departure and unit are fixed. If the stay really did change: cancel the invoice, change the booking, issue a new one.`}
          </span>
        </p>
      )}

      <fieldset disabled={dateLocked} className="min-w-0 space-y-4 disabled:opacity-60">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('booking.arrival')}>
            {/*
              * Die Abreise rückt mit, sobald die Anreise sie einholen würde.
              *
              * `min` am Abreisefeld allein genügt NICHT: es begrenzt nur dessen
              * eigenen Auswahldialog und sagt nichts darüber, was passiert, wenn
              * die ANREISE nach hinten wandert. Genau so entstand ein Aufenthalt
              * mit negativer Länge — `nights()` liefert dann eine negative Zahl,
              * die Preisrechnung ein leeres Ergebnis, und die Datenbank weist den
              * Bereich beim Speichern zurück. `NewBookingSheet` macht das seit
              * jeher so; hier fehlte es.
              */}
            <Input type="date" value={draft.arrival}
              onChange={(e) => {
                const arrival = e.target.value
                if (!arrival) return
                set(arrival >= draft.departure
                  ? { arrival, departure: addDays(arrival, 1) }
                  : { arrival })
              }} />
          </Field>
          <Field label={t('booking.departure')}>
            <Input type="date" value={draft.departure} min={addDays(draft.arrival, 1)}
              onChange={(e) => set({ departure: e.target.value })} />
          </Field>
        </div>

        {/* Die Einheit gehört mit in die Sperre, nicht nur die zwei Datumsfelder: die
            ausgestellte Rechnung nennt auch, WO der Gast gewohnt hat. Ein offenes
            Auswahlfeld böte hier eine Änderung an, die es nicht geben darf. */}
        <Field label={t('booking.unit')}>
          {/*
            * Deaktivierte Einheiten sind hier nicht wählbar — überall sonst waren
            * sie es schon nicht (`CalendarView`, `NewBookingSheet`, `lib/enquiry`
            * filtern alle auf `active`), nur dieses Blatt bot sie weiter an. Eine
            * stillgelegte Wohnung im Auswahlfeld ist eine Einladung, sie zu
            * belegen.
            *
            * Die AKTUELL gewählte bleibt trotzdem drin, auch wenn sie inzwischen
            * deaktiviert wurde. Sonst zeigte eine bestehende Buchung stumm eine
            * andere Einheit an als die, auf der sie liegt — und das Speichern
            * würde sie tatsächlich verschieben.
            */}
          <Select value={draft.unit_id} onChange={(e) => set({ unit_id: e.target.value })}>
            {store.db.units
              .filter((u) => u.active || u.id === draft.unit_id)
              .map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
          </Select>
        </Field>
      </fieldset>

      {!isBlock && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Field label={t('common.adults')}>
              <Input
                type="number" min={0} max={20} inputMode="numeric"
                value={leerLassen('adults', draft.adults)}
                onChange={(e) => zahlTippen('adults', e.target.value,
                  (n) => set({ adults: n }))}
                onBlur={() => zahlVerlassen('adults')}
              />
            </Field>
            <Field label={t('common.children')}>
              <Input
                type="number" min={0} max={20} inputMode="numeric"
                value={leerLassen('children', draft.children)}
                onChange={(e) => zahlTippen('children', e.target.value, (n) => set({
                  children: n,
                  children_ages: draft.children_ages.slice(0, n),
                }))}
                onBlur={() => zahlVerlassen('children')}
              />
            </Field>
            <Field label="Status">
              <Select value={draft.status}
                onChange={(e) => {
                  const status = e.target.value as BookingStatus
                  // A hold without a deadline is the thing this feature exists to
                  // prevent, so picking Option proposes one straight away: a week,
                  // or arrival if that comes sooner.
                  if (status === 'option' && !draft.option_until) {
                    const week = addDays(todayISO(), OPTION_DEFAULT_DAYS)
                    set({ status, option_until: week < draft.arrival ? week : draft.arrival })
                  } else {
                    set({ status })
                  }
                }}>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{t(`status.${s}` as never)}</option>
                ))}
              </Select>
            </Field>
          </div>

          {draft.status === 'option' && (
            <Field
              label={t('booking.optionUntil')}
              hint={t('booking.optionUntilHint')}
            >
              <Input
                type="date"
                value={draft.option_until ?? ''}
                max={draft.arrival}
                onChange={(e) => set({ option_until: e.target.value || null })}
              />
            </Field>
          )}

          {draft.children > 0 && (
            <Field label={`${t('common.children')} — ${t('touristTax.exemptUnder', { n: 15 })}`}>
              <div className="flex gap-2 flex-wrap">
                {Array.from({ length: draft.children }, (_, i) => (
                  <Input
                    key={i}
                    type="number" min={0} max={17}
                    className="w-20"
                    placeholder="Alter"
                    value={draft.children_ages[i] ?? ''}
                    onChange={(e) => {
                      const ages = [...draft.children_ages]
                      ages[i] = Number(e.target.value)
                      set({ children_ages: ages.filter((a) => !Number.isNaN(a)) })
                    }}
                  />
                ))}
              </div>
            </Field>
          )}

          {optionalFees.length > 0 && (
            <Field label={t('booking.extras')}>
              <div className="flex flex-wrap gap-2">
                {optionalFees.map((f) => {
                  const on = draft.optional_fee_ids?.includes(f.id) ?? false
                  return (
                    <button
                      key={f.id}
                      onClick={() => set({
                        optional_fee_ids: on
                          ? (draft.optional_fee_ids ?? []).filter((x) => x !== f.id)
                          : [...(draft.optional_fee_ids ?? []), f.id],
                      })}
                      className={cx(
                        'px-3 py-1.5 rounded-full text-sm border',
                        on ? 'bg-accent-soft text-accent border-accent'
                           : 'bg-surface-2 text-muted border-border',
                      )}
                    >
                      {pick(f.name)} · {formatMoney(f.amount)}
                    </button>
                  )
                })}
              </div>
            </Field>
          )}

          <Field label={t('booking.guest')}>
            {guest ? (
              <div className="bg-surface-2 rounded-xl px-3 py-2.5 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1 truncate">{store.guestName(draft.guest_id)}</div>
                  {/* In die Kartei, statt Vorlieben und Allergien hier unterzubringen:
                      das Buchungsblatt verknüpft den Gast, die Kartei kennt ihn. Ohne
                      diesen Weg wäre die Kartei ein Ort, den man nur zufällig findet. */}
                  {/*
                    * ERST SPEICHERN, DANN WECHSELN.
                    *
                    * Dieser Sprung räumt Kalender und Blatt ab. Bei einer im
                    * Kalender aufgezogenen Buchung lebt die aber nur im Zustand
                    * des Kalenders — sie war damit weg, ohne Rückfrage und ohne
                    * Hinweis. Auf dem Telefon sitzt der Knopf direkt neben dem
                    * gerade ausgewählten Gastnamen und ist leicht zu treffen.
                    *
                    * Am 31.07.2026 gemeldet, mit Recht: „so ist die App nutzlos".
                    *
                    * Scheitert das Speichern — belegter Zeitraum, fehlender Gast —,
                    * wird NICHT navigiert. Die Meldung dazu steht oben im Blatt und
                    * wäre nach einem Wechsel nicht mehr zu sehen.
                    */}
                  {savedYet && (
                    <Button size="sm" variant="ghost"
                      title={de ? 'Speichern und in der Gastkartei öffnen'
                                : 'Save and open in the guest card'}
                      onClick={async () => {
                        commitGuest()
                        if (await store.saveBooking(draft)) {
                          navigate(`/guests?gast=${draft.guest_id}`)
                        }
                      }}>
                      <IdCard size={15} />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => set({ guest_id: null })}>
                    {t('common.edit')}
                  </Button>
                </div>
                {/* The invoice needs a postal address, so it is editable right here
                    rather than behind a separate guest screen. */}
                {/* Typed into local state and committed on blur. Writing to the store
                    on every keystroke re-rendered the whole sheet — including the price
                    breakdown — once per character, which is what made the fields jump
                    on a phone. */}
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    className="col-span-2" placeholder="E-Mail" type="email"
                    inputMode="email" autoComplete="email"
                    value={guestForm.email}
                    onChange={(e) => setGuestForm({ ...guestForm, email: e.target.value })}
                    onBlur={commitGuest}
                  />
                  <Input
                    className="col-span-2" placeholder={t('booking.address')}
                    value={guestForm.address}
                    onChange={(e) => setGuestForm({ ...guestForm, address: e.target.value })}
                    onBlur={commitGuest}
                  />
                  <Input
                    placeholder={t('booking.postcode')} value={guestForm.postcode}
                    onChange={(e) => setGuestForm({ ...guestForm, postcode: e.target.value })}
                    onBlur={commitGuest}
                  />
                  <Input
                    placeholder={t('booking.city')} value={guestForm.city}
                    onChange={(e) => setGuestForm({ ...guestForm, city: e.target.value })}
                    onBlur={commitGuest}
                  />
                  <Select
                    className="col-span-2"
                    value={guestForm.language}
                    onChange={(e) => {
                      const language = e.target.value
                      setGuestForm({ ...guestForm, language })
                      if (guest) void store.saveGuest({ ...guest, ...guestForm, language })
                    }}
                    aria-label={t('booking.docLanguage')}
                  >
                    <option value="de">{t('booking.docLanguage')}: Deutsch</option>
                    <option value="en">{t('booking.docLanguage')}: English</option>
                  </Select>
                </div>
              </div>
            ) : newGuest ? (
              /*
               * Einen Gast anlegen, in einem Vorgang.
               *
               * WARUM ES DEN EIGENEN KNOPF BRAUCHT: bisher entstand ein Gast nur, wenn
               * man zwei Buchstaben tippte und dann den Vorschlag „„Name" neu" traf.
               * Wer den nicht gesehen hat, kam zu dem Schluss, dass sich hier gar keine
               * Gäste anlegen lassen — und mit dem Namenszwang stünde er vor einer Tür,
               * deren Klinke er nicht findet.
               *
               * WARUM VOR- UND NACHNAME GETRENNT: `guests` speichert beides getrennt,
               * und die Anrede im Schriftverkehr braucht den Vornamen für sich. Das
               * Zerlegen einer Eingabe („Anna Maria Huber") rät und rät oft falsch.
               *
               * E-Mail und Telefon stehen dabei, weil sie zum Anlegen gehören: ohne
               * E-Mail kann kein Angebot rausgehen, und beides danach in Feldern
               * nachzutragen, die erst nach dem Verknüpfen erscheinen, versteht
               * niemand als denselben Vorgang. Die Anschrift bleibt bewusst draußen —
               * die braucht erst die Rechnung, und sie steht gleich darunter.
               */
              <div className="bg-surface-2 rounded-xl px-3 py-2.5 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    autoFocus
                    placeholder={de ? 'Vorname' : 'First name'}
                    value={newGuest.first_name}
                    onChange={(e) => setNewGuest({ ...newGuest, first_name: e.target.value })}
                  />
                  <Input
                    placeholder={de ? 'Nachname' : 'Last name'}
                    value={newGuest.last_name}
                    onChange={(e) => setNewGuest({ ...newGuest, last_name: e.target.value })}
                  />
                  <Input
                    className="col-span-2" type="email" inputMode="email" autoComplete="email"
                    placeholder="E-Mail"
                    value={newGuest.email}
                    onChange={(e) => setNewGuest({ ...newGuest, email: e.target.value })}
                  />
                  <Input
                    className="col-span-2" type="tel" inputMode="tel"
                    placeholder={de ? 'Telefon' : 'Phone'}
                    value={newGuest.phone}
                    onChange={(e) => setNewGuest({ ...newGuest, phone: e.target.value })}
                  />
                </div>
                {verdacht.length > 0 && (
                  <div className="rounded-xl bg-warn-soft text-warn px-3 py-2 space-y-1.5">
                    <p className="text-xs font-medium">
                      {de
                        ? (verdacht.length === 1
                          ? 'Könnte eine Dublette sein — diesen Gast gibt es womöglich schon:'
                          : 'Könnte eine Dublette sein — diese Gäste gibt es womöglich schon:')
                        : 'This might be a duplicate — these guests already exist:'}
                    </p>
                    {verdacht.map((v) => (
                      <button
                        key={v.guest.id}
                        className="w-full text-left text-xs underline underline-offset-2"
                        /* Wie in der Trefferliste: bei offener Tastatur kommt ein
                           `click` nicht an. Siehe Kommentar dort. */
                        onPointerDown={(e) => {
                          e.preventDefault()
                          set({ guest_id: v.guest.id })
                          setNewGuest(null)
                          setGuestQuery('')
                        }}
                      >
                        {v.guest.first_name} {v.guest.last_name}
                        {v.guest.email ? ` · ${v.guest.email}` : ''}
                        {' — '}
                        {de ? 'diesen nehmen' : 'use this one'}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    size="sm" variant="primary" className="flex-1"
                    disabled={!(newGuest.first_name.trim() || newGuest.last_name.trim())}
                    onClick={async () => {
                      const saved = await store.saveGuest({
                        id: crypto.randomUUID(),
                        tenant_id: store.db.tenant.id,
                        first_name: newGuest.first_name.trim(),
                        last_name: newGuest.last_name.trim(),
                        email: newGuest.email.trim() || undefined,
                        phone: newGuest.phone.trim() || undefined,
                        language: store.db.tenant.locale,
                        blacklisted: false,
                      })
                      set({ guest_id: saved.id })
                      setNewGuest(null)
                      setGuestQuery('')
                    }}
                  >
                    <UserPlus size={15} /> {de ? 'Anlegen und verknüpfen' : 'Create and link'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setNewGuest(null)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Input
                  placeholder={t('common.search')}
                  value={guestQuery}
                  onChange={(e) => setGuestQuery(e.target.value)}
                />
                {guestMatches.length > 0 && (
                  <ul className="mt-1 border border-border rounded-xl overflow-hidden">
                    {guestMatches.map((g) => (
                      <li key={g.id}>
                        {/*
                          * `onPointerDown` UND `preventDefault`, nicht `onClick`.
                          *
                          * Die Tastatur des Telefons verkleinert den Anzeigebereich
                          * (`interactive-widget=resizes-content` in `index.html`).
                          * Beim Antippen verliert das Suchfeld zuerst den Fokus, die
                          * Tastatur fährt ein, die Seite wird schlagartig höher — und
                          * der `click` landet ins Leere, weil der Knopf inzwischen
                          * woanders steht. Der Gast liess sich damit auf dem Telefon
                          * NICHT auswählen, während am Schreibtisch alles ging.
                          *
                          * `preventDefault` verhindert den Fokuswechsel, `pointerdown`
                          * greift, bevor irgendetwas sich bewegt.
                          *
                          * Am 31.07.2026 gemeldet, nachdem der erste Fehler an
                          * derselben Stelle behoben war.
                          */}
                        <button
                          className="w-full text-left px-3 py-2 hover:bg-surface-2"
                          onPointerDown={(e) => {
                            e.preventDefault()
                            set({ guest_id: g.id })
                            setGuestQuery('')
                          }}
                        >
                          <div className="text-sm">{g.first_name} {g.last_name}</div>
                          <div className="text-xs text-muted">{g.email}</div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {guestQuery.trim().length >= 2 && (
                  <Button
                    size="sm"
                    className="mt-2 w-full"
                    /* Wie oben: die Tastatur verschiebt die Seite, bevor ein
                       `click` ankommt. Siehe Kommentar bei der Trefferliste. */
                    onPointerDown={async (e) => {
                      e.preventDefault()
                      const parts = guestQuery.trim().split(/\s+/)
                      const saved = await store.saveGuest({
                        id: crypto.randomUUID(),
                        tenant_id: store.db.tenant.id,
                        first_name: parts.length > 1 ? parts.slice(0, -1).join(' ') : parts[0]!,
                        last_name: parts.length > 1 ? parts[parts.length - 1]! : '',
                        language: store.db.tenant.locale,
                        blacklisted: false,
                      })
                      set({ guest_id: saved.id })
                      setGuestQuery('')
                    }}
                  >
                    <UserPlus size={15} /> „{guestQuery.trim()}" {t('common.new')}
                  </Button>
                )}
                {/* Sichtbar, ohne dass man vorher tippt: das Suchfeld allein sieht aus
                    wie eine Suche in einer Kartei, in der man noch nichts anlegen kann.
                    Was schon getippt wurde, wandert als Namensvorschlag mit. */}
                <Button
                  size="sm" variant="secondary" className="mt-2 w-full"
                  /* Wie oben. */
                  onPointerDown={(e) => {
                    e.preventDefault()
                    const parts = guestQuery.trim().split(/\s+/).filter(Boolean)
                    setNewGuest({
                      first_name: parts.length > 1 ? parts.slice(0, -1).join(' ') : (parts[0] ?? ''),
                      last_name: parts.length > 1 ? parts[parts.length - 1]! : '',
                      email: '', phone: '',
                    })
                  }}
                >
                  <UserPlus size={15} /> {de ? 'Neuen Gast anlegen' : 'Create a new guest'}
                </Button>
              </>
            )}
          </Field>
        </>
      )}

      {/* ---------------- price breakdown ---------------- */}
      {!isBlock && priced && stayNights > 0 && (
        <section className="bg-surface-2 rounded-2xl p-3">
          <div className="flex items-baseline justify-between mb-2">
            <h3 className="font-medium text-sm">{t('booking.breakdown')}</h3>
            <span className="text-xs text-muted">
              {stayNights} {t(stayNights === 1 ? 'common.night' : 'common.nights')} · {unit?.name}
            </span>
          </div>
          {/* Ein Sonderpreis, den niemand sieht, ist eine Fehlerquelle: er wandert in
              jedes Angebot und jede Rechnung, und ein halbes Jahr später weiß keiner
              mehr, warum die Woche billiger war als die Preisliste. Also steht er
              über der Aufstellung — mitsamt dem Preis, von dem abgewichen wurde. */}
          {priced.nightlyOverride != null && (
            <div className="mb-2 flex items-center gap-1.5 rounded-lg bg-accent-soft text-accent px-2 py-1 text-xs">
              <Tag size={13} className="shrink-0" />
              <span>
                {de ? 'Vereinbarter Preis' : 'Agreed rate'} {formatMoney(priced.nightlyOverride)}
                {' '}{de ? 'je Nacht statt' : 'per night instead of'}
                {' '}{formatMoney(priced.listAverageNightly)}
              </span>
            </div>
          )}
          <table className="w-full text-sm">
            <tbody>
              {priced.lines.map((l, i) => (
                <tr key={i} className="border-b border-border last:border-0">
                  <td className="py-1.5 pr-2">
                    <div>{l.nameJson ? pick(l.nameJson) : t(l.label as never)}</div>
                    <div className="text-xs text-muted">
                      {l.qty} × {formatMoney(l.unitPrice)}
                      {l.vatRate > 0 && ` · ${l.vatRate}% ${t('common.vat')}`}
                    </div>
                  </td>
                  <td className="py-1.5 text-right tabular-nums whitespace-nowrap">
                    {formatMoney(l.gross)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="text-xs text-muted">
                <td className="pt-2">{t('common.net')}</td>
                <td className="pt-2 text-right tabular-nums">{formatMoney(priced.totals.net)}</td>
              </tr>
              <tr className="text-xs text-muted">
                <td>{t('common.vat')}</td>
                <td className="text-right tabular-nums">{formatMoney(priced.totals.vat)}</td>
              </tr>
              <tr className="font-semibold">
                <td className="pt-1">{t('common.total')}</td>
                <td className="pt-1 text-right tabular-nums">{formatMoney(priced.totals.gross)}</td>
              </tr>
            </tfoot>
          </table>
        </section>
      )}

      {/* ---------------- money ---------------- */}
      {/*
        * Der ausgehandelte Preis. Zwei verschiedene Zusagen, deshalb zwei Felder:
        *
        * „Preis je Nacht" ist ein anderer Preis — er ersetzt die Preisliste für jede
        * Nacht und rechnet weiter mit, wenn der Aufenthalt verschoben oder verlängert
        * wird. Der Platzhalter zeigt, was ohne ihn gälte; ohne diesen Vergleich weiß
        * niemand, wovon er gerade abweicht.
        *
        * „Nachlass" ist ein Abzug vom fertigen Preis, und der Grund gehört dazu: er
        * steht als Text auf der Rechnung, und ein Abzug ohne Begründung ist der
        * Posten, den der Gast als Erstes hinterfragt.
        */}
      {!isBlock && priced && stayNights > 0 && (
        <section className="bg-surface-2 rounded-2xl p-3">
          {!priceOpen ? (
            <button
              type="button"
              className="flex items-center gap-2 text-sm text-muted"
              onClick={() => setPriceOpen(true)}
            >
              <Tag size={15} /> {de ? 'Preis anpassen' : 'Adjust price'}
            </button>
          ) : (
            <div className="space-y-2.5">
              <div className="grid grid-cols-2 gap-3">
                <Field
                  label={de ? 'Preis je Nacht' : 'Rate per night'}
                  hint={de
                    ? `Leer = Preisliste (${formatMoney(priced.listAverageNightly)})`
                    : `Empty = rate card (${formatMoney(priced.listAverageNightly)})`}
                >
                  <Input
                    type="number" step="0.01" min={0}
                    placeholder={priced.listAverageNightly.toFixed(2)}
                    value={draft.nightly_rate_override ?? ''}
                    onChange={(e) => set({
                      nightly_rate_override: e.target.value === '' ? null : Number(e.target.value),
                    })}
                  />
                </Field>
                <Field label={de ? 'Nachlass' : 'Discount'}>
                  <Input
                    type="number" step="0.01" min={0}
                    placeholder="0,00"
                    value={draft.discount_amount || ''}
                    onChange={(e) => set({ discount_amount: Number(e.target.value) || 0 })}
                  />
                </Field>
              </div>
              {(draft.discount_amount ?? 0) > 0 && (
                <Field label={de ? 'Grund für den Nachlass' : 'Reason for the discount'}>
                  <Input
                    placeholder={de ? 'z. B. Gutschein, Stammgast' : 'e.g. voucher, regular guest'}
                    value={draft.discount_reason ?? ''}
                    onChange={(e) => set({ discount_reason: e.target.value || null })}
                  />
                </Field>
              )}
              <button
                type="button"
                className="text-xs text-muted underline"
                onClick={() => {
                  set({ nightly_rate_override: null, discount_amount: 0, discount_reason: null })
                  setPriceOpen(false)
                }}
              >
                {de ? 'Preisliste verwenden' : 'Use the rate card'}
              </button>
            </div>
          )}
        </section>
      )}

      {!isBlock && (
        <section className="grid grid-cols-2 gap-3">
          <Field
            label={t('booking.prepayment')}
            hint={prepaymentPolicy.mode === 'percent'
              ? `${prepaymentPolicy.percent} % ${t('booking.ofTotal')}`
              : prepaymentPolicy.mode === 'fixed'
                ? formatMoney(prepaymentPolicy.fixed)
                : undefined}
          >
            {/* Shows the amount that will actually be stored on save, rather than
                a bare 0 that looks like "no prepayment". */}
            <Input type="number" step="0.01"
              value={draft.prepayment_amount || suggestedPrepayment}
              onChange={(e) => set({ prepayment_amount: Number(e.target.value) })} />
          </Field>
          <Field label={t('booking.deposit')}>
            <Input type="number" step="0.01" value={draft.deposit_amount}
              onChange={(e) => set({
                deposit_amount: Number(e.target.value),
                deposit_status: Number(e.target.value) > 0 ? 'due' : 'not_required',
              })} />
          </Field>
        </section>
      )}

      {!isBlock && (
        <Prepayment
          booking={draft}
          amount={draft.prepayment_amount || suggestedPrepayment}
          onPatch={set}
        />
      )}

      {!isBlock && <CheckinCard booking={draft} />}

      {!isBlock && draft.deposit_amount > 0 && (
        <div className="flex items-center justify-between bg-surface-2 rounded-xl px-3 py-2.5">
          <div className="text-sm">
            {t('deposit.title')}
            <span className="text-muted"> · {formatMoney(draft.deposit_amount)}</span>
          </div>
          <Select
            className="w-auto"
            value={draft.deposit_status}
            onChange={(e) => set({ deposit_status: e.target.value as Booking['deposit_status'] })}
          >
            {(['not_required', 'due', 'held', 'partially_retained', 'retained', 'released'] as const)
              .map((s) => <option key={s} value={s}>{t(`deposit.${s}` as never)}</option>)}
          </Select>
        </div>
      )}

      {!isBlock && paid !== 0 && (
        <div className="text-sm flex justify-between px-1">
          <span className="text-muted">{t('common.paid')}</span>
          <span className="tabular-nums">
            {formatMoney(paid)}
            <span className={cx('ml-2', outstanding > 0 ? 'text-warn' : 'text-ok')}>
              ({t('common.open')} {formatMoney(outstanding)})
            </span>
          </span>
        </div>
      )}

      {/*
        * Was der Gast selbst geschrieben hat.
        *
        * `public_submit_enquiry` legt die Nachricht aus dem Anfrageformular in
        * `bookings.notes` ab — und bis zum 28.07.2026 zeigte dieses Blatt
        * ausschließlich `internal_notes`. Die Folge: „Wir kommen mit unserer
        * Tochter (7). Bitte ein ruhiges Zimmer." stand in der Datenbank und
        * erreichte den Vermieter nie. Beim ersten Durchlauf der ganzen Kette fiel
        * es auf; die Nachricht war der einzige Grund, aus dem er das Alter des
        * Kindes und den Zimmerwunsch überhaupt hätte erfahren können.
        *
        * Sie steht ÜBER der internen Notiz und ist nicht bearbeitbar: es ist ein
        * Zitat des Gasts, kein Feld des Vermieters. Wer es ändern könnte, hätte
        * später keine verlässliche Auskunft mehr darüber, was der Gast wollte.
        */}
      {draft.notes?.trim() && (
        <Field label={de ? 'Nachricht des Gasts' : 'The guest wrote'}>
          <p className="text-sm bg-surface-2 rounded-xl px-3 py-2.5 whitespace-pre-line">
            {draft.notes}
          </p>
        </Field>
      )}

      <Field label={t('booking.internalNotes')}>
        <Input value={draft.internal_notes ?? ''}
          onChange={(e) => set({ internal_notes: e.target.value })} />
      </Field>

      {/*
        * Rückfrage statt stiller zweiter Rechnung.
        *
        * „Rechnung erstellen" ließ sich beliebig oft drücken, und jedes Mal entstand
        * eine weitere Rechnung über denselben Aufenthalt — bemerkt wird das meist
        * erst, wenn der Gast zwei Belege in der Hand hält oder die Auswertung den
        * Umsatz doppelt zählt. Die Rückfrage sagt deshalb nicht nur „gibt es schon",
        * sondern zeigt WELCHE (Nummer, Datum, Betrag, Stand) und bietet den Weg
        * dorthin an: in neun von zehn Fällen wollte der Vermieter genau die.
        */}
      {!isBlock && invoiceClash && (
        <div className="rounded-xl bg-warn-soft text-warn px-3 py-2.5 text-sm space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <div>
              <div>
                {de
                  ? 'Für diesen Aufenthalt gibt es bereits eine Rechnung:'
                  : 'There is already an invoice for this stay:'}
              </div>
              <div className="font-medium">
                {invoiceClash.number ?? (de ? 'Entwurf ohne Nummer' : 'Draft without a number')}
                {' · '}{formatDate(invoiceClash.issue_date)}
                {' · '}{formatMoney(invoiceClash.total_gross)}
                {' · '}{invoiceStateLabel(invoiceClash)}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary"
              onClick={() => { const id = invoiceClash.id; onClose(); navigate(`/document/invoice/${id}`) }}>
              {de ? 'Bestehende öffnen' : 'Open the existing one'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void makeInvoice()}>
              {de ? 'Trotzdem eine zweite' : 'Create a second one anyway'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setInvoiceClash(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      )}

      {!isBlock && (
        <div className="flex gap-2">
          <Button
            className="flex-1"
            onClick={async () => {
              if (!(await store.saveBooking(draft))) return
              const id = await store.createQuote(draft.id)
              onClose()
              if (id) navigate(`/document/quote/${id}`)
            }}
          >
            <FileText size={16} /> {t('booking.createQuote')}
          </Button>
          <Button
            className="flex-1"
            onClick={() => {
              if (bookingInvoices.length > 0) { setInvoiceClash(bookingInvoices[0]!); return }
              void makeInvoice()
            }}
          >
            <ReceiptEuro size={16} /> {t('booking.createInvoice')}
          </Button>
        </div>
      )}

      {/*
        * Der Weg zum Gast. Er steht UNTER den beiden Belegknöpfen, weil er ihnen
        * zeitlich folgt: erst gibt es ein Angebot oder eine Rechnung, dann geht sie
        * hinaus. Verschickt wird sie nicht von hier — StaySuite tritt als Absender
        * nicht mehr auf, der Vermieter schreibt aus seinem eigenen Postfach.
        */}
      {!isBlock && draft.guest_id && (
        <p className="text-xs text-muted">
          {de
            ? 'Angebot und Rechnung gehen aus Ihrem eigenen Mailprogramm hinaus: Beleg öffnen, drucken oder als PDF sichern und selbst an den Gast senden.'
            : 'The quote and the invoice go out from your own mail program: open the document, print it or save it as a PDF, and send it to the guest yourself.'}
        </p>
      )}

      <p className="text-xs text-muted text-center">
        {draft.reference || '—'} · {formatDate(draft.arrival)} – {formatDate(draft.departure)}
      </p>
    </Sheet>
  )
}

function Warning({ tone, text }: { tone: 'warn' | 'danger'; text: string }) {
  return (
    <div className={cx(
      'flex items-start gap-2 rounded-xl px-3 py-2 text-sm',
      tone === 'danger' ? 'bg-danger-soft text-danger' : 'bg-warn-soft text-warn',
    )}>
      <AlertTriangle size={16} className="shrink-0 mt-0.5" />
      <span>{text}</span>
    </div>
  )
}


/**
 * Die Anzahlung einer Buchung: was verlangt wurde, was davon eingegangen ist, und die
 * beiden Wege, auf denen es eingehen kann.
 *
 * WARUM ES DEN HANDEINTRAG BRAUCHT: die allermeisten Anzahlungen kommen als
 * Überweisung auf das Konto des Vermieters. Von der erfährt StaySuite nie etwas. Ohne
 * einen Knopf, der „ist da" sagt, bleibt jede Buchung auf ewig unbezahlt, und die
 * Schlussrechnung verlangt den vollen Betrag ein zweites Mal — genau der Fehler, den
 * ein Gast sofort sieht und der Vertrauen kostet.
 *
 * Die Zahlung wird als Zahlung gebucht, nicht als Häkchen an der Buchung. Nur so
 * findet die Rechnung sie wieder, nur so steht sie in der Kassenübersicht, und nur so
 * ist später nachvollziehbar, wann und auf welchem Weg das Geld kam. Der Stempel auf
 * der Buchung (`prepaid_at`) ist die Folge davon, nicht die Wahrheit selbst.
 *
 * Der Stripe-Weg schreibt dieselbe Zahlung, nur eben serverseitig. Beim Öffnen wird
 * einmal nachgefragt, ob inzwischen bezahlt wurde — der Gast zahlt bei Stripe, und
 * niemand erzählt es uns von selbst. Das ist billiger als ein Webhook und ehrlicher
 * als zu hoffen.
 */
function Prepayment({ booking, amount, onPatch }: {
  booking: Booking
  amount: number
  onPatch: (patch: Partial<Booking>) => void
}) {
  const { lang, formatMoney, formatDate } = useI18n()
  const store = useStore()
  const de = lang === 'de'

  const received = store.db.payments
    .filter((p) => p.booking_id === booking.id && p.kind === 'prepayment')
    .sort((a, b) => a.received_on.localeCompare(b.received_on))
  const receivedTotal = Math.round(received.reduce((a, p) => a + p.amount, 0) * 100) / 100
  const openAmount = Math.round((amount - receivedTotal) * 100) / 100

  const [entry, setEntry] = useState<
    { amount: string; received_on: string; method: Payment['method'] } | null>(null)
  const [url, setUrl] = useState<string | null>(booking.stripe_prepay_url ?? null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const connected = Boolean(store.db.tenant.stripe_account_id)

  // Einmal je geöffneter Buchung. Ohne die Sperre stößt jedes Neuladen des Stores den
  // Abgleich erneut an, und der kostet einen Stripe-Aufruf.
  const synced = useRef(false)
  useEffect(() => { synced.current = false; setUrl(booking.stripe_prepay_url ?? null) },
    [booking.id, booking.stripe_prepay_url])

  useEffect(() => {
    if (synced.current) return
    if (!store.live || !supabase || booking.prepaid_at || !booking.stripe_prepay_session_id) return
    synced.current = true
    void supabase.functions
      .invoke('stripe-connect', { body: { action: 'prepay_sync', booking_id: booking.id } })
      .then(({ data }) => {
        // Die Zahlung schreibt die Function selbst — hier muss nur der Store nachziehen,
        // sonst bliebe sie bis zum nächsten Laden unsichtbar.
        if ((data as { paid?: boolean })?.paid) {
          onPatch({ prepaid_at: new Date().toISOString() })
          store.reload()
        }
      })
      .catch(() => { /* stiller Abgleich, kein Grund den Vermieter zu behelligen */ })
  }, [booking.id, booking.prepaid_at, booking.stripe_prepay_session_id, store.live])

  if (amount <= 0 && received.length === 0) return null

  const METHODS: Payment['method'][] = ['bank_transfer', 'cash', 'card', 'stripe', 'other']
  const methodLabel = (m: Payment['method']) => (de
    ? { bank_transfer: 'Überweisung', cash: 'Bar', card: 'Karte', stripe: 'Stripe',
        paypal: 'PayPal', other: 'Anders' }[m]
    : { bank_transfer: 'Bank transfer', cash: 'Cash', card: 'Card', stripe: 'Stripe',
        paypal: 'PayPal', other: 'Other' }[m])

  return (
    <section className="bg-surface-2 rounded-2xl p-3 space-y-2.5">
      <div className="flex items-baseline justify-between">
        <h3 className="font-medium text-sm">{de ? 'Anzahlung' : 'Deposit payment'}</h3>
        <span className="text-xs">
          {openAmount <= 0.004 ? (
            <span className="text-ok">
              {de ? 'Vollständig eingegangen' : 'Received in full'}
            </span>
          ) : (
            <span className="text-warn">
              {de ? 'Offen' : 'Open'} {formatMoney(openAmount)}
              {receivedTotal > 0 && ` ${de ? 'von' : 'of'} ${formatMoney(amount)}`}
            </span>
          )}
        </span>
      </div>

      {/* Was eingegangen ist, einzeln — samt Weg zurück, wenn es ein Irrtum war. */}
      {received.map((p) => (
        <div key={p.id} className="flex items-center gap-2 text-sm bg-surface rounded-xl px-3 py-2">
          <span className="text-ok tabular-nums">{formatMoney(p.amount)}</span>
          <span className="text-muted text-xs flex-1 min-w-0 truncate">
            {formatDate(p.received_on)} · {methodLabel(p.method)}
            {p.note && ` · ${p.note}`}
          </span>
          <button
            className="text-muted hover:text-danger shrink-0"
            aria-label={de ? 'Zahlung entfernen' : 'Remove payment'}
            onClick={() => {
              if (!window.confirm(de
                ? 'Diese Zahlung wieder entfernen? Die Buchung gilt dann als nicht angezahlt.'
                : 'Remove this payment? The booking will count as not prepaid again.')) return
              void store.removePayment(p.id)
              if (received.length === 1) onPatch({ prepaid_at: null })
            }}
          >
            <Trash2 size={15} />
          </button>
        </div>
      ))}

      {/* Handeintrag: Betrag, Tag und Weg. Alles drei vorbelegt, damit der Normalfall
          — „der volle Betrag ist heute überwiesen worden" — zwei Klicks bleibt. */}
      {entry ? (
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            <Field label={de ? 'Betrag' : 'Amount'}>
              <Input type="number" step="0.01" value={entry.amount}
                onChange={(e) => setEntry({ ...entry, amount: e.target.value })} />
            </Field>
            <Field label={de ? 'Eingang am' : 'Received on'}>
              <Input type="date" value={entry.received_on}
                onChange={(e) => setEntry({ ...entry, received_on: e.target.value })} />
            </Field>
            <Field label={de ? 'Weg' : 'Method'}>
              <Select value={entry.method}
                onChange={(e) => setEntry({ ...entry, method: e.target.value as Payment['method'] })}>
                {METHODS.map((m) => <option key={m} value={m}>{methodLabel(m)}</option>)}
              </Select>
            </Field>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="primary" className="flex-1" disabled={busy}
              onClick={async () => {
                const value = Math.round(Number(entry.amount) * 100) / 100
                if (!(value > 0)) { setError(de ? 'Betrag fehlt.' : 'Amount missing.'); return }
                setBusy(true); setError(null)
                await store.addPayment({
                  booking_id: booking.id,
                  invoice_id: null,
                  kind: 'prepayment',
                  method: entry.method,
                  amount: value,
                  currency: booking.currency ?? store.db.tenant.currency,
                  received_on: entry.received_on,
                })
                // Den Entwurf mitziehen: sonst schreibt „Speichern" den alten,
                // leeren Stempel über den gerade gesetzten.
                onPatch({ prepaid_at: new Date(`${entry.received_on}T12:00:00`).toISOString() })
                setBusy(false); setEntry(null)
              }}>
              {de ? 'Eingang buchen' : 'Record payment'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setEntry(null); setError(null) }}>
              {de ? 'Abbrechen' : 'Cancel'}
            </Button>
          </div>
        </div>
      ) : openAmount > 0.004 && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="secondary"
            onClick={() => setEntry({
              amount: String(openAmount),
              received_on: todayISO(),
              method: 'bank_transfer',
            })}>
            <Check size={15} /> {de ? 'Anzahlung erhalten' : 'Deposit received'}
          </Button>

          {store.live && connected && (
            <Button size="sm" variant={url ? 'ghost' : 'secondary'} disabled={busy}
              onClick={async () => {
                setBusy(true); setError(null)
                try {
                  if (!supabase) throw new Error('no_backend')
                  const { data, error: e } = await supabase.functions.invoke('stripe-connect', {
                    body: { action: 'prepay_link', booking_id: booking.id },
                  })
                  if (e) throw e
                  setUrl((data as { url?: string })?.url ?? null)
                } catch (err) {
                  setError(String((err as Error).message))
                }
                setBusy(false)
              }}>
              <CreditCard size={15} />
              {url
                ? (de ? 'Neuen Zahlungslink' : 'New payment link')
                : (de ? 'Zahlungslink erzeugen' : 'Create payment link')}
            </Button>
          )}
        </div>
      )}

      {url && openAmount > 0.004 && (
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={url}
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 min-w-0 text-xs bg-surface rounded-xl px-3 py-2"
          />
          <Button size="sm" variant="secondary" onClick={() => {
            void navigator.clipboard?.writeText(url)
            setCopied(true)
            window.setTimeout(() => setCopied(false), 1500)
          }}>
            {copied ? (de ? 'Kopiert' : 'Copied') : (de ? 'Kopieren' : 'Copy')}
          </Button>
        </div>
      )}

      {store.live && !connected && openAmount > 0.004 && (
        <p className="text-xs text-muted">
          {de
            ? 'Für einen Zahlungslink muss in den Einstellungen ein Stripe-Konto verbunden sein. Eine Überweisung kannst du oben auch ohne Stripe eintragen.'
            : 'Connect a Stripe account in Settings to send a payment link. A bank transfer can be recorded above without Stripe.'}
        </p>
      )}

      {error && <p className="text-xs text-danger">{error}</p>}
    </section>
  )
}
